import { SelectableValue, GroupOfSelectable } from "@tenforce/product-fields"

import { dataAPI } from "./api"

const OTHER_GROUP_ID = 0

export type LoadChildrenResponse = {
    children: GroupOfSelectable[]
    count: number
}

const selectorToSingularTypeMapper: Record<SelectorModels.Type, string> = {
    "TF.OptionItemField": "TF.Option",
    "TF.OptionsItemField": "TF.Option",
    "TF.StatusItemField": "TF.Status",
    "TF.UserItemField": "TF.User",
    "TF.UsersItemField": "TF.User",
    "TF.ApplicationItemField": "TF.Application",
    "TF.ApplicationVersionItemField": "TF.ApplicationVersion",
    "TF.PriorityItemField": "TF.ItemPriority",
    "TF.ItemItemField": "TF.Item",
    "TF.ItemsItemField": "TF.Item",
    "TF.FieldDefinition": "TF.FieldDefinition",
    "TF.OrganizationItemField": "TF.OrganizationUnit",
    "TF.UserGroupItemField": "TF.UserGroup",
    "TF.UserGroupsItemField": "TF.UserGroup",
} as const

export const selectorToGroupTypeMapper: Record<string, string> = {
    "TF.OptionItemField": "Group",
    "TF.OptionsItemField": "Group",
    "TF.UserItemField": "OrganizationUnit",
    "TF.UsersItemField": "OrganizationUnit",
    "TF.ItemItemField": "Item",
    "TF.ItemsItemField": "Item",
}

export function selectorModelMapper<Type extends SelectorModels.Type>(
    type: Type,
): (data: SelectorModels.TypeModel[Type]) => Omit<
    SelectableValue,
    "isSelected"
> & {
    groupData: undefined | SelectorModels.Group
} {
    switch (type) {
        case "TF.OptionItemField":
        case "TF.OptionsItemField":
            return (data) => {
                const casted = data as SelectorModels.Option
                return {
                    id: casted.Id,
                    name: casted.Label,
                    groupData: {
                        Id: casted?.Group?.Id ?? OTHER_GROUP_ID,
                        Label: casted?.Group?.Label ?? "",
                    },
                }
            }
        case "TF.UserItemField":
        case "TF.UsersItemField":
            return (data) => {
                const casted = data as SelectorModels.User
                return {
                    id: casted.Id,
                    name: `${casted.FirstName} ${casted.LastName}`,
                    groupData: {
                        Id: casted.OrganizationUnit?.Id || OTHER_GROUP_ID,
                        Label: casted.OrganizationUnit?.Label || "",
                    },
                }
            }
        case "TF.StatusItemField":
        case "TF.PriorityItemField":
            return (data) => {
                const casted = data as SelectorModels.Status
                return {
                    id: casted.Id,
                    name: casted.Label,
                    groupData: undefined,
                }
            }

        case "TF.ApplicationItemField":
        case "TF.ApplicationVersionItemField":
            return (data) => {
                const casted = data as SelectorModels.Application
                return {
                    id: casted.Id,
                    name: casted.Label,
                    groupData: undefined,
                }
            }

        case "TF.ItemItemField":
        case "TF.ItemsItemField":
            return (data) => {
                const casted = data as SelectorModels.Item
                return {
                    id: casted.Id,
                    name: casted.Label,
                    groupData: undefined,
                }
            }

        case "TF.FieldDefinition":
            return (data) => {
                const casted = data as SelectorModels.FieldDefinition
                const parsedId = casted.Id?.startsWith("CF")
                    ? parseInt(casted.Id?.substring(2))
                    : parseInt(casted.Id?.substring(2)) * -1
                let parsedGroupId = 0
                if (casted.Group) {
                    parsedGroupId = casted.Group.Id.startsWith("CFG")
                        ? parseInt(casted.Group.Id.substring(3))
                        : parseInt(casted.Group.Id.substring(3)) * -1
                }

                return {
                    id: parsedId,
                    name: casted.Label,
                    groupData: {
                        Id: parsedGroupId || OTHER_GROUP_ID,
                        Label: casted.Group?.Label || "",
                    },
                }
            }

        case "TF.OrganizationItemField":
            return (data) => {
                const casted = data as SelectorModels.OrganizationItem
                return {
                    id: casted.Id,
                    name: casted.Label,
                    groupData: undefined,
                }
            }
        case "TF.UserGroupItemField":
        case "TF.UserGroupsItemField":
            return (data) => {
                const casted = data as SelectorModels.UserGroup
                return {
                    id: casted.Id,
                    name: casted.Label,
                    groupData: undefined,
                }
            }

        default:
            return (data) => ({
                id: data.Id as number,
                name: "",
                groupData: undefined,
            })
    }
}

export const typeHasGroups = (type: SelectorModels.Type): boolean => {
    switch (type) {
        case "TF.StatusItemField":
        case "TF.PriorityItemField":
        case "TF.ApplicationItemField":
        case "TF.ApplicationVersionItemField":
        case "TF.ItemItemField":
        case "TF.UserGroupItemField":
        case "TF.UserGroupsItemField":
            return false
        default:
            return true
    }
}

const filterQueryMapper = (filter: string): string => {
    if (!filter) {
        return ""
    }
    return `contains(Label,'${filter}')`
}

export const getChildrenForGroup = async <T extends SelectorModels.Type>(
    groupId: number,
    itemId: number,
    fieldId: string,
    type: T,
    filter = "",
    skip = 0,
    top = 20,
): Promise<LoadChildrenResponse> => {
    const filterQuery = filterQueryMapper(filter)

    const groupType = selectorToGroupTypeMapper[type]
    const filterByGroup = groupId
        ? `${groupType}/Id eq ${groupId}`
        : `${groupType} eq null`

    const parameters = {
        $filter: filter
            ? `${filterByGroup} and ${filterQuery}`
            : `${filterByGroup}`,
        $top: top,
        $skip: skip,
        ...(type === "TF.ItemItemField" || type === "TF.ItemsItemField"
            ? {}
            : { $expand: groupType, $count: true }),
    }

    return dataAPI
        .getJSON<Api.ODataGetArrayResponse<SelectorModels.TypeModel[T]>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${type}/SelectableValues`,
            parameters,
        )
        .then((response) => {
            return {
                children: parseSelectorData(response.value, type),
                count: response["@count"],
            } as LoadChildrenResponse
        })
        .catch(() => {
            return { children: [], count: 0 } as LoadChildrenResponse
        })
}

export const getGroups = async <T extends SelectorModels.Type>(
    itemId: number,
    fieldId: string,
    type: T,
    filter = "",
    skip = 0,
    top = 20,
): Promise<GroupOfSelectable | null> => {
    const filterQuery = filterQueryMapper(filter)
    const groupType = selectorToGroupTypeMapper[type]
    const completeFilterQuery = filter
        ? `filter(${filterQuery})/groupby((${groupType}/Label,${groupType}/Id),aggregate($count as Total))`
        : `groupby((${groupType}/Label,${groupType}/Id),aggregate($count as Total))`

    const parameters = {
        $apply: completeFilterQuery,
        $top: top,
        $skip: skip,
        ...(type === "TF.ItemItemField" || type === "TF.ItemsItemField"
            ? {}
            : {
                  $count: true,
              }),
    }

    return dataAPI
        .getJSON<
            Api.ODataGetArrayResponse<
                SelectorModels.TypeModel[T] & SelectorModels.AggregateResponse
            >
        >(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${type}/SelectableValues`,
            parameters,
        )
        .then((response) => {
            return parseGroupData(response, type)
        })
        .catch(() => null)
}

export const getValues = async <T extends SelectorModels.Type>(
    itemId: number,
    fieldId: string,
    type: T,
    filter = "",
    skip = 0,
    top = 1000,
): Promise<GroupOfSelectable | null> => {
    const filterQuery = filterQueryMapper(filter)

    // expand and count not supported
    const parameters = {
        ...(filterQuery
            ? {
                  $filter: filterQuery,
              }
            : {}),
        $top: top,
        $skip: skip,
        $count: true,
        ...(type === "TF.ItemItemField" || type === "TF.ItemsItemField"
            ? {}
            : { $expand: selectorToGroupTypeMapper[type] }),
    }

    return dataAPI
        .getJSON<
            | Api.ODataGetArrayResponse<SelectorModels.TypeModel[T]>
            | Api.ODataGetResourceResponse<SelectorModels.TypeModel[T]>
        >(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${type}/SelectableValues`,
            parameters,
        )
        .then((response) => {
            return {
                ...parseResponseData(response, type),
                totalChildrenCount: response["@count"],
            } as GroupOfSelectable
        })
        .catch(() => null)
}

export const getSelectedValues = async <T extends SelectorModels.Type>(
    itemId: number,
    fieldId: string,
    type: T,
): Promise<GroupOfSelectable | null> => {
    return dataAPI
        .getJSON<
            | Api.ODataGetArrayResponse<SelectorModels.TypeModel[T]>
            | Api.ODataGetResourceResponse<SelectorModels.TypeModel[T]>
        >(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${type}/Value`,
            type === "TF.ItemItemField" || type === "TF.ItemsItemField"
                ? {}
                : { $expand: selectorToGroupTypeMapper[type] },
        )
        .then((response) => parseResponseData(response, type))
        .catch(() => null)
}

export const selectValue = async <T extends SelectorModels.Type>(
    valueId: number,
    itemId: number,
    fieldId: string,
    type: T,
): Promise<GroupOfSelectable | null> => {
    return await dataAPI
        .patchJSON<
            Api.ODataGetResourceValueResponse<SelectorModels.TypeModel[T]>
        >(
            `ItemFields(ItemId=${itemId},FieldDefinitionId='${fieldId}')/${type}`,
            {
                "@type": type,
                value: {
                    "@type": selectorToSingularTypeMapper[type],
                    id: valueId,
                },
            },
        )
        .then((response) => parseValueResponseData(response, type))
        .catch(() => null)
}

export const clearSingleSelect = async (
    itemId: number,
    fieldId: string,
    type: SelectorModels.Type,
): Promise<void> => {
    await dataAPI.remove(
        `ItemFields(ItemId=${itemId},FieldDefinitionId='${fieldId}')/${type}/Value/$ref`,
    )
}

export const sendMultiSelectDeltaValue = async <T extends SelectorModels.Type>(
    toSelect: number[],
    toDeselect: number[],
    itemId: number,
    fieldId: string,
    type: T,
): Promise<GroupOfSelectable | null> => {
    return await dataAPI
        .patchJSON<
            Api.ODataGetArrayResponseUppercase<SelectorModels.TypeModel[T]>
        >(
            `ItemFields(ItemId=${itemId},FieldDefinitionId='${fieldId}')/${type}`,
            {
                "@type": type,
                "Value@delta": [
                    ...toSelect.map((valueId) => ({
                        "@type": selectorToSingularTypeMapper[type],
                        id: valueId,
                    })),
                    ...toDeselect.map((valueId) => ({
                        "@removed": {
                            reason: "changed",
                        },
                        "@type": selectorToSingularTypeMapper[type],
                        id: valueId,
                    })),
                ],
            },
        )
        .then((response) => parseValueResponseData(response, type))
        .catch(() => null)
}

export const parseResponseData = <T extends SelectorModels.Type>(
    response:
        | Api.ODataGetArrayResponse<SelectorModels.TypeModel[T]>
        | Api.ODataGetResourceResponse<SelectorModels.TypeModel[T]>,
    type: T,
): GroupOfSelectable | null => {
    if (!("value" in response) && response.Id === undefined) {
        return null
    }

    const responseChildren = "value" in response ? response.value : [response]

    return createSelectorGroups(responseChildren, type)
}

export const parseValueResponseData = <T extends SelectorModels.Type>(
    response:
        | Api.ODataGetArrayResponseUppercase<SelectorModels.TypeModel[T]>
        | Api.ODataGetResourceValueResponse<SelectorModels.TypeModel[T]>,
    type: T,
): GroupOfSelectable | null => {
    if (!("Value" in response)) {
        return null
    }
    const responseChildren = Array.isArray(response.Value)
        ? response.Value
        : [response.Value]

    return createSelectorGroups(responseChildren, type)
}

export const parseGroupData = <T extends SelectorModels.Type>(
    response: Api.ODataGetArrayResponse<
        SelectorModels.TypeModel[T] & SelectorModels.AggregateResponse
    >,
    type: T,
): GroupOfSelectable | null => {
    if (!("value" in response)) {
        return null
    }

    const children = "value" in response ? response.value : [response]
    const mapper = selectorModelMapper(type)
    const responseChildren = children.map((child) => {
        const mapped = mapper(child)
        const name = mapped.groupData?.Label
        const mappedChild = {
            id: mapped.groupData?.Id,
            name: !!name ? name : "Other",
            totalChildrenCount: child.Total,
            selectedChildrenCount: 0,
        }
        return mappedChild
    })

    return {
        id: -1,
        children: responseChildren,
        name: "",
        selectedChildrenCount: 0,
        totalChildrenCount: responseChildren.length,
    } as GroupOfSelectable
}

export const parseSelectorData = <T extends SelectorModels.Type>(
    response: SelectorModels.TypeModel[T] | SelectorModels.TypeModel[T][],
    type: T,
): GroupOfSelectable[] => {
    if (!response) {
        return []
    }

    const responseChildren = Array.isArray(response) ? response : [response]
    const mapper = selectorModelMapper(type)
    const childrenToBe = responseChildren.map((child) => {
        const mapped = mapper(child)
        const mappedChild = {
            ...mapped,
            totalChildrenCount: 0,
            selectedChildrenCount: 0,
        }
        return mappedChild
    })
    return childrenToBe
}

export const createSelectorGroups = <T extends SelectorModels.Type>(
    responseChildren: SelectorModels.TypeModel[T][],
    type: T,
): GroupOfSelectable | null => {
    const mapper = selectorModelMapper(type)

    const groupsDict: Record<number, GroupOfSelectable> = {}
    if (typeHasGroups(type)) {
        groupsDict[OTHER_GROUP_ID] = {
            id: OTHER_GROUP_ID,
            children: [],
            name: "Other",
            selectedChildrenCount: 0,
            totalChildrenCount: 0,
        }
    }
    const children: GroupOfSelectable[] = []
    for (const child of responseChildren) {
        const mapped = mapper(child)
        const mappedChild = {
            ...mapped,
            totalChildrenCount: 0,
            selectedChildrenCount: 0,
        }
        if (mapped.groupData) {
            let targetGroup = groupsDict[mapped.groupData.Id]
            if (!groupsDict[mapped.groupData.Id]) {
                groupsDict[mapped.groupData.Id] = targetGroup = {
                    id: mapped.groupData.Id,
                    name: mapped.groupData.Label,
                    totalChildrenCount: 0,
                    selectedChildrenCount: 0,
                    children: [],
                }
            }
            targetGroup.children?.push(mappedChild)

            targetGroup.selectedChildrenCount =
                targetGroup.children?.length ?? 0
        } else {
            children.push(mappedChild)
        }
    }

    children.push(
        ...Object.values(groupsDict)
            .filter((group) => group?.children?.length)
            .map((group) => {
                const mapped = {
                    ...group,
                    totalChildrenCount: group?.children?.length,
                } as GroupOfSelectable
                return mapped
            }),
    )

    children.sort((a, b) => (a.id > b.id ? 1 : -1))
    return {
        id: -1,
        name: "",
        totalChildrenCount: children.length,
        selectedChildrenCount: 0,
        children,
    }
}
